<template>
  <div class="container-fluid">
    <div class="row">
      <!-- Lista de referencias disponibles -->
      <div class="col-md-3">
        <div class="card">
          <div class="card-header">
            <h5 class="card-title mb-0">Referencias Disponibles</h5>
          </div>
          <div class="card-body">
            <ContentSkeleton v-if="isLoading" variant="list" :rows="5" />
            <div v-else>
              <div class="list-group">
                <button
                  v-for="reference in availableReferences"
                  :key="`${reference.sourceModel}-${reference.sourceField}`"
                  class="list-group-item list-group-item-action"
                  :class="{
                    actie:
                      selectedReference &&
                      selectedReference.sourceField === reference.sourceField,
                    'has-mappings': hasMappingsForReference(reference),
                  }"
                  @click="selectReference(reference)"
                >
                  <div>
                    <small> {{ reference.sourceFieldLabel }} </small>
                    <div
                      v-if="hasMappingsForReference(reference)"
                      class="mapping-badge"
                    >
                      <div class="w-100">
                        <i class="fas fa-link"></i>
                        {{ getMappingCount(reference) }} mapeos
                      </div>
                      <div class="w-100">
                        <small class="d-block text-muted">
                          {{ getMappingDirectionText(reference) }}
                        </small>
                      </div>
                      <!-- Indicador de consistencia -->
                      <div
                        v-if="hasConsistencyMappings(reference)"
                        class="w-100 mt-1"
                      >
                        <small class="text-success">
                          <i class="fas fa-link"></i> Mapeos con consistencia
                        </small>
                      </div>
                    </div>
                  </div>
                  <div
                    class="d-flex justify-content-between align-items-center"
                  >
                    <span class="badge bg-primary rounded-pill">
                      {{ reference.sourceModel }} >
                      {{ reference.targetModel }}
                    </span>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Área de mapeo -->
      <div class="col-md-9" v-if="selectedReference">
        <div class="card">
          <div class="card-header">
            <div class="d-flex justify-content-between align-items-center">
              <h5 class="card-title mb-0">
                Mapeo: {{ selectedReference.sourceModel }} →
                {{ selectedReference.targetModel }}
              </h5>
              <div class="mapping-direction-selector">
                <label class="form-label me-2">Dirección del mapeo:</label>
                <select
                  v-model="mappingDirection"
                  class="form-select form-select-sm"
                  style="width: auto"
                >
                  <option value="AtoB">
                    {{ selectedReference.sourceModel }} →
                    {{ selectedReference.targetModel }}
                  </option>
                  <option value="BtoA">
                    {{ selectedReference.targetModel }} →
                    {{ selectedReference.sourceModel }}
                  </option>
                  <option value="bidirectional">Bidireccional</option>
                </select>
              </div>
            </div>
          </div>
          <div class="card-body">
            <div class="row">
              <!-- Mensaje informativo para modo bidireccional -->
              <div
                v-if="mappingDirection === 'bidirectional'"
                class="col-12 mb-3"
              >
                <div class="alert alert-info">
                  <i class="fas fa-info-circle"></i>
                  <strong>Modo Bidireccional:</strong> Se crearán
                  automáticamente dos mapeos: uno de
                  {{ selectedReference.sourceModel }} →
                  {{ selectedReference.targetModel }} y otro de
                  {{ selectedReference.targetModel }} →
                  {{ selectedReference.sourceModel }}
                </div>
              </div>

              <!-- Mensaje informativo sobre consistencia -->
              <div class="col-12 mb-3">
                <div class="alert alert-warning">
                  <i class="fas fa-info-circle"></i>
                  <strong>Consistencia:</strong> Cuando marques el checkbox de
                  "Consistencia" en un mapeo, se establecerá una relación
                  constante entre los campos. Esto significa que cuando se edite
                  el campo origen, el campo destino se actualizará
                  automáticamente para mantener la sincronización.
                </div>
              </div>
              <!-- Campos del modelo origen -->
              <div class="col-md-4">
                <h6>{{ leftTitle }}</h6>
                <div
                  class="border rounded p-3 mapping-container"
                  style="height: 600px; overflow-y: auto; position: relative"
                >
                  <div
                    v-for="field in leftFields"
                    :key="field.key"
                    class="mb-3"
                  >
                    <div
                      class="field-mapping-card"
                      :class="{
                        'is-mapped': selectedMappings[field.key],
                        'is-selected':
                          mappingMode.isActive &&
                          mappingMode.sourceField === field.key,
                      }"
                      :id="`source-${field.key}`"
                    >
                      <div class="field-info">
                        <h6 class="mb-1">{{ field.label }}</h6>
                        <small class="text-muted">{{ field.key }}</small>
                      </div>
                      <div class="mapping-controls">
                        <button
                          class="btn btn-sm"
                          :class="{
                            'btn-outline-primary':
                              !mappingMode.isActive ||
                              mappingMode.sourceField !== field.key,
                            'btn-primary':
                              mappingMode.isActive &&
                              mappingMode.sourceField === field.key,
                          }"
                          @click="selectFieldForMapping(field)"
                        >
                          <i
                            class="fas"
                            :class="{
                              'fa-arrow-right':
                                !mappingMode.isActive ||
                                mappingMode.sourceField !== field.key,
                              'fa-times':
                                mappingMode.isActive &&
                                mappingMode.sourceField === field.key,
                            }"
                          ></i>
                          {{
                            mappingMode.isActive &&
                            mappingMode.sourceField === field.key
                              ? "Cancelar"
                              : "Mapear"
                          }}
                        </button>
                      </div>
                    </div>
                    <!-- Campos de grupo -->
                    <div v-if="field.type === 'group'" class="ms-4 mt-2">
                      <div
                        v-for="subField in field.fields"
                        :key="subField.key"
                        class="field-mapping-card sub-field"
                        :class="{
                          'is-mapped':
                            selectedMappings[field.key]?.groupMappings[
                              subField.key
                            ],
                          'is-selected':
                            mappingMode.isActive &&
                            mappingMode.sourceField === subField.key,
                        }"
                        :id="`source-${field.key}-${subField.key}`"
                      >
                        <div class="field-info">
                          <h6 class="mb-1">{{ subField.label }}</h6>
                          <small class="text-muted">{{ subField.key }}</small>
                        </div>
                        <div class="mapping-controls">
                          <button
                            class="btn btn-sm"
                            :class="{
                              'btn-outline-primary':
                                !mappingMode.isActive ||
                                mappingMode.sourceField !== subField.key,
                              'btn-primary':
                                mappingMode.isActive &&
                                mappingMode.sourceField === subField.key,
                            }"
                            @click="selectSubFieldForMapping(field, subField)"
                          >
                            <i
                              class="fas"
                              :class="{
                                'fa-arrow-right':
                                  !mappingMode.isActive ||
                                  mappingMode.sourceField !== subField.key,
                                'fa-times':
                                  mappingMode.isActive &&
                                  mappingMode.sourceField === subField.key,
                              }"
                            ></i>
                            {{
                              mappingMode.isActive &&
                              mappingMode.sourceField === subField.key
                                ? "Cancelar"
                                : "Mapear"
                            }}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Panel central con mapeos -->
              <div
                class="col-md-4 p-0 m-0 d-flex flex-column justify-content-center align-items-center"
              >
                <div class="mapping-panel">
                  <div
                    v-for="(mapping, sourceField) in selectedMappings"
                    :key="sourceField"
                    class="mapping-item"
                  >
                    <div
                      v-if="mapping.targetField"
                      class="mapping-row"
                      :class="{ 'consistency-enabled': mapping.consistency }"
                    >
                      <div class="mapping-field source">
                        {{ getFieldLabel(sourceField, leftFields) }}
                      </div>
                      <div class="mapping-arrow">
                        <i class="fas fa-arrow-right"></i>
                      </div>
                      <div class="mapping-field target">
                        {{ getFieldLabel(mapping.targetField, rightFields) }}
                      </div>
                    </div>
                    <!-- Mapeos de grupo -->
                    <template v-if="mapping.isGroup">
                      <div
                        v-for="(groupMapping, subField) in getMappedGroupFields(
                          mapping.groupMappings
                        )"
                        :key="`${sourceField}-${subField}`"
                        class="mapping-row sub-mapping"
                        :class="{
                          'consistency-enabled': groupMapping.consistency,
                        }"
                      >
                        <div class="mapping-field source">
                          {{
                            getSubFieldLabel(sourceField, subField, leftFields)
                          }}
                        </div>
                        <div class="mapping-arrow">
                          <i class="fas fa-arrow-right"></i>
                        </div>
                        <div class="mapping-field target">
                          {{
                            getFieldLabel(groupMapping.targetField, rightFields)
                          }}
                        </div>
                      </div>
                    </template>
                  </div>
                </div>
              </div>

              <!-- Campos del modelo destino -->
              <div class="col-md-4">
                <h6>{{ rightTitle }}</h6>
                <div
                  class="border rounded p-3 mapping-container"
                  style="height: 600px; overflow-y: auto; position: relative"
                >
                  <div
                    v-for="field in rightFields"
                    :key="field.key"
                    class="mb-3"
                  >
                    <div
                      class="field-mapping-card"
                      :class="{
                        'is-mapped': isFieldMapped(field.key),
                        'is-selected':
                          mappingMode.isActive &&
                          mappingMode.targetField === field.key,
                      }"
                      :id="`target-${field.key}`"
                    >
                      <div class="field-info">
                        <h6 class="mb-1">{{ field.label }}</h6>
                        <small class="text-muted">{{ field.key }}</small>
                      </div>
                      <div class="mapping-controls">
                        <button
                          class="btn btn-sm btn-outline-primary"
                          @click="selectTargetField(field)"
                          :class="{ '': isFieldMapped(field.key) }"
                        >
                          <i class="fas fa-arrow-left"></i> Mapear
                        </button>
                      </div>
                    </div>
                    <!-- Campos de grupo -->
                    <div v-if="field.type === 'group'" class="ms-4 mt-2">
                      <div
                        v-for="subField in field.fields"
                        :key="subField.key"
                        class="field-mapping-card sub-field"
                        :class="{
                          'is-mapped': isSubFieldMapped(
                            field.key,
                            subField.key
                          ),
                          'is-selected':
                            mappingMode.isActive &&
                            mappingMode.targetField === subField.key,
                        }"
                        :id="`target-${field.key}-${subField.key}`"
                      >
                        <div class="field-info">
                          <h6 class="mb-1">{{ subField.label }}</h6>
                          <small class="text-muted">{{ subField.key }}</small>
                        </div>
                        <div class="mapping-controls">
                          <button
                            class="btn btn-sm btn-outline-primary"
                            @click="selectTargetSubField(field, subField)"
                            :class="{
                              '': isSubFieldMapped(field.key, subField.key),
                            }"
                          >
                            <i class="fas fa-arrow-left"></i> Mapear
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Botones de acción -->
            <div class="mt-3">
              <button class="btn btn-primary" @click="saveMapping">
                Guardar Mapeo
              </button>
              <button class="btn btn-secondary ms-2" @click="clearSelection">
                Limpiar Selección
              </button>
            </div>

            <!-- Resumen de consistencia -->
            <div v-if="getConsistencySummary().total > 0" class="mt-3">
              <div class="alert alert-success">
                <i class="fas fa-link"></i>
                <strong>Resumen de Consistencia:</strong>
                {{ getConsistencySummary().enabled }} de
                {{ getConsistencySummary().total }} mapeos tienen consistencia
                habilitada.
                <small class="d-block mt-1">
                  Los campos con consistencia se sincronizarán automáticamente
                  cuando se editen los campos origen.
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { wencesApi } from "@/api/wencesApi";
import Swal from "sweetalert2";
import ContentSkeleton from "@/components/common/ContentSkeleton.vue";

export default {
  name: "ModelReferenceMapping",
  components: { ContentSkeleton },
  data() {
    return {
      isLoading: false,
      availableReferences: [],
      selectedReference: null,
      sourceFields: [],
      targetFields: [],
      selectedMappings: {},
      currentMapping: null,
      mappingMode: {
        isActive: false,
        sourceField: null,
        sourceFieldType: null,
        isGroup: false,
        groupField: null,
      },
      existingMappings: [],
      mappingDirection: "AtoB",
    };
  },
  async mounted() {
    await this.loadAvailableReferences();
    window.addEventListener("scroll", this.updateMappingPositions);
    window.addEventListener("resize", this.updateMappingPositions);
  },
  computed: {
    // Campos que se muestran en el lado izquierdo según la dirección
    leftFields() {
      if (this.mappingDirection === "BtoA") {
        return this.sourceFields; // Para B → A, mostrar campos del modelo B (sourceFields) como origen
      }
      return this.sourceFields; // Mostrar campos del modelo origen
    },
    // Campos que se muestran en el lado derecho según la dirección
    rightFields() {
      if (this.mappingDirection === "BtoA") {
        return this.targetFields; // Para B → A, mostrar campos del modelo A (targetFields) como destino
      }
      return this.targetFields; // Mostrar campos del modelo destino
    },
    // Título del lado izquierdo
    leftTitle() {
      if (this.mappingDirection === "BtoA") {
        return `Campos Origen (${this.selectedReference?.targetModel})`;
      }
      return `Campos Origen (${this.selectedReference?.sourceModel})`;
    },
    // Título del lado derecho
    rightTitle() {
      if (this.mappingDirection === "BtoA") {
        return `Campos Destino (${this.selectedReference?.sourceModel})`;
      }
      return `Campos Destino (${this.selectedReference?.targetModel})`;
    },
  },
  watch: {
    async mappingDirection() {
      // Resetear mapeos cuando cambia la dirección
      this.$set(this, "selectedMappings", {});
      this.mappingMode.isActive = false;
      this.mappingMode.sourceField = null;

      // Recargar campos cuando cambia la dirección
      await this.reloadFieldsForDirection();
    },
  },
  methods: {
    async loadAvailableReferences() {
      this.isLoading = true;
      try {
        const response = await wencesApi.get(
          "/api/gotrading/model-reference-mapping/available-references"
        );
        if (response.data && response.data.success) {
          const baseReferences = (response.data.data || []).map((ref) => ({
            ...ref,
            hasMappings: ref.hasMappings || false,
            mappingCount: ref.mappingCount || 0,
          }));

          // Cargar mapeos existentes para detectar referencias inversas
          await this.loadExistingMappings();

          // Crear referencias inversas basadas en los mapeos existentes
          const inverseReferences = [];
          this.existingMappings.forEach((mapping) => {
            // Buscar si ya existe una referencia directa para este mapeo
            const existingDirectRef = baseReferences.find(
              (ref) =>
                ref.sourceModel === mapping.sourceModel &&
                ref.targetModel === mapping.targetModel &&
                ref.sourceField === mapping.sourceField
            );

            // Si no existe una referencia directa, crear una inversa
            if (!existingDirectRef) {
              // Buscar si ya existe una referencia inversa
              const existingInverseRef = inverseReferences.find(
                (ref) =>
                  ref.sourceModel === mapping.sourceModel &&
                  ref.targetModel === mapping.targetModel &&
                  ref.sourceField === mapping.sourceField
              );

              if (!existingInverseRef) {
                inverseReferences.push({
                  sourceModel: mapping.sourceModel,
                  targetModel: mapping.targetModel,
                  sourceField: mapping.sourceField,
                  sourceFieldLabel: mapping.sourceField,
                  hasMappings: true,
                  mappingCount: 0, // Se calculará después
                });
              }
            }
          });

          // También buscar mapeos que son inversos de las referencias base
          baseReferences.forEach((baseRef) => {
            // Buscar si existe un mapeo inverso (B → A) para esta referencia (A → B)
            const inverseMapping = this.existingMappings.find(
              (mapping) =>
                mapping.sourceModel === baseRef.targetModel &&
                mapping.targetModel === baseRef.sourceModel &&
                mapping.sourceField === baseRef.sourceField
            );

            // Si existe un mapeo inverso, crear una referencia inversa
            if (inverseMapping) {
              const existingInverseRef = inverseReferences.find(
                (ref) =>
                  ref.sourceModel === baseRef.targetModel &&
                  ref.targetModel === baseRef.sourceModel &&
                  ref.sourceField === baseRef.sourceField
              );

              if (!existingInverseRef) {
                inverseReferences.push({
                  sourceModel: baseRef.targetModel,
                  targetModel: baseRef.sourceModel,
                  sourceField: baseRef.sourceField,
                  sourceFieldLabel: baseRef.sourceField,
                  hasMappings: true,
                  mappingCount: 0, // Se calculará después
                });
              }
            }
          });

          // Combinar referencias directas e inversas
          this.availableReferences = [...baseReferences, ...inverseReferences];
        } else {
          console.error("Error en la respuesta:", response.data);
          await Swal.fire({
            title: "Error",
            text: "Error al cargar las referencias disponibles",
            icon: "error",
          });
        }
      } catch (error) {
        console.error("Error loading references:", error);
        await Swal.fire({
          title: "Error",
          text: "Error al cargar las referencias disponibles",
          icon: "error",
        });
      } finally {
        this.isLoading = false;
      }
    },
    async loadExistingMappings() {
      try {
        const response = await wencesApi.get(
          "/api/gotrading/model-reference-mapping"
        );
        if (response.data && response.data.success) {
          this.existingMappings = response.data.data || [];
          console.log("Mapeos existentes cargados:", this.existingMappings);

          // Verificar que los mapeos tengan la propiedad consistency
          this.existingMappings.forEach((mapping) => {
            if (mapping.mappings) {
              mapping.mappings.forEach((m) => {
                if (m.consistency === undefined) {
                  console.warn("Mapeo sin propiedad consistency:", m);
                }
                if (m.isGroup && m.groupMappings) {
                  Object.values(m.groupMappings).forEach((gm) => {
                    if (gm.consistency === undefined) {
                      console.warn(
                        "GroupMapping sin propiedad consistency:",
                        gm
                      );
                    }
                  });
                }
              });
            }
          });
        }
      } catch (error) {
        console.error("Error loading existing mappings:", error);
      }
    },
    async selectReference(reference) {
      if (!reference || !reference.sourceModel || !reference.targetModel) {
        console.error("Referencia inválida:", reference);
        return;
      }

      this.selectedReference = reference;
      this.$set(this, "selectedMappings", {});

      try {
        // Cargar campos del modelo origen
        const sourceResponse = await wencesApi.get(
          `/api/gotrading/model-reference-mapping/model-fields/${reference.sourceModel}`
        );
        if (sourceResponse.data && sourceResponse.data.success) {
          this.sourceFields = sourceResponse.data.data || [];
        } else {
          throw new Error("Error al cargar campos del modelo origen");
        }

        // Cargar campos del modelo destino
        const targetResponse = await wencesApi.get(
          `/api/gotrading/model-reference-mapping/model-fields/${reference.targetModel}`
        );
        if (targetResponse.data && targetResponse.data.success) {
          this.targetFields = targetResponse.data.data || [];
        } else {
          throw new Error("Error al cargar campos del modelo destino");
        }

        // Cargar mapeo existente si existe
        const mappingsResponse = await wencesApi.get(
          "/api/gotrading/model-reference-mapping"
        );
        if (mappingsResponse.data && mappingsResponse.data.success) {
          // Buscar mapeo A → B
          const existingMappingAtoB = mappingsResponse.data.data.find(
            (m) =>
              m.sourceModel === reference.sourceModel &&
              m.sourceField === reference.sourceField &&
              m.targetModel === reference.targetModel
          );

          // Buscar mapeo B → A
          const existingMappingBtoA = mappingsResponse.data.data.find(
            (m) =>
              m.sourceModel === reference.targetModel &&
              m.sourceField === reference.sourceField &&
              m.targetModel === reference.sourceModel
          );

          // Determinar qué mapeo usar y establecer la dirección
          if (existingMappingAtoB && existingMappingBtoA) {
            // Ambos mapeos existen, usar bidireccional
            this.mappingDirection = "bidirectional";
            this.currentMapping = existingMappingAtoB;
          } else if (existingMappingAtoB) {
            // Solo existe A → B
            this.mappingDirection = "AtoB";
            this.currentMapping = existingMappingAtoB;
          } else if (existingMappingBtoA) {
            // Solo existe B → A
            this.mappingDirection = "BtoA";
            this.currentMapping = existingMappingBtoA;
          }

          // Inicializar selectedMappings con los valores existentes
          if (this.currentMapping) {
            const initialMappings = {};
            this.currentMapping.mappings.forEach((mapping) => {
              // Inicializar mapeos de grupo con consistencia
              const groupMappings = {};
              if (mapping.isGroup && mapping.groupMappings) {
                Object.entries(mapping.groupMappings).forEach(
                  ([subField, groupMapping]) => {
                    groupMappings[subField] = {
                      targetField: groupMapping.targetField,
                      consistency: false,
                    };
                  }
                );
              }

              initialMappings[mapping.sourceField] = {
                targetField: mapping.targetField,
                isGroup: mapping.isGroup,
                groupMappings: groupMappings,
                consistency: false,
              };
            });
            this.$set(this, "selectedMappings", initialMappings);
          }

          // Si es un mapeo B → A, necesitamos ajustar los selectedMappings
          // porque los campos mostrados en la UI están invertidos
          if (this.mappingDirection === "BtoA" && this.currentMapping) {
            console.log("Ajustando mapeos para B → A:", this.currentMapping);

            // Crear un nuevo objeto de mapeos ajustado
            const adjustedMappings = {};

            this.currentMapping.mappings.forEach((mapping) => {
              console.log("Mapeo original:", mapping);
              console.log(
                "sourceFields disponibles:",
                this.sourceFields.map((f) => f.key)
              );
              console.log("Buscando campo:", mapping.sourceField);

              // Para mapeos B → A, el sourceField del mapeo corresponde a campos del modelo B
              // que ahora se muestran correctamente en el lado izquierdo
              // El targetField corresponde a campos del modelo A que se muestran en el lado derecho

              const sourceField = this.sourceFields.find(
                (field) => field.key === mapping.sourceField
              );
              console.log("Campo encontrado:", sourceField);

              if (sourceField) {
                // Inicializar mapeos de grupo con consistencia
                const groupMappings = {};
                if (mapping.isGroup && mapping.groupMappings) {
                  Object.entries(mapping.groupMappings).forEach(
                    ([subField, groupMapping]) => {
                      groupMappings[subField] = {
                        targetField: groupMapping.targetField,
                        consistency: false,
                      };
                    }
                  );
                }

                adjustedMappings[mapping.sourceField] = {
                  targetField: mapping.targetField,
                  isGroup: mapping.isGroup,
                  groupMappings: groupMappings,
                  consistency: false,
                };
                console.log(
                  "Mapeo ajustado:",
                  mapping.sourceField,
                  "→",
                  mapping.targetField
                );
              } else {
                console.log(
                  "Campo NO encontrado en sourceFields:",
                  mapping.sourceField
                );
              }
            });

            // Usar $set para asegurar que Vue detecte los cambios
            this.$set(this, "selectedMappings", adjustedMappings);
            console.log("SelectedMappings finales:", this.selectedMappings);
            console.log(
              "leftFields:",
              this.leftFields.map((f) => f.key)
            );
            console.log(
              "rightFields:",
              this.rightFields.map((f) => f.key)
            );
          }
        }
      } catch (error) {
        console.error("Error loading fields:", error);
        await Swal.fire({
          title: "Error",
          text: "Error al cargar los campos de los modelos",
          icon: "error",
        });
      }
    },
    selectFieldForMapping(field) {
      if (this.mappingMode.isActive) {
        this.mappingMode.isActive = false;
        this.mappingMode.sourceField = null;
        delete this.selectedMappings[field.key];
        return;
      }

      this.mappingMode.isActive = true;
      this.mappingMode.sourceField = field.key;
      this.mappingMode.sourceFieldType = field.type;
      this.mappingMode.isGroup = false;

      if (!this.selectedMappings[field.key]) {
        this.selectedMappings[field.key] = {
          targetField: "",
          isGroup: field.type === "group",
          groupMappings: {},
          consistency: false,
        };
      }
    },
    selectSubFieldForMapping(field, subField) {
      if (this.mappingMode.isActive) {
        this.mappingMode.isActive = false;
        this.mappingMode.sourceField = null;
        if (
          this.selectedMappings[field.key] &&
          this.selectedMappings[field.key].groupMappings
        ) {
          delete this.selectedMappings[field.key].groupMappings[subField.key];
          if (
            Object.keys(this.selectedMappings[field.key].groupMappings)
              .length === 0
          ) {
            delete this.selectedMappings[field.key];
          }
        }
        return;
      }

      this.mappingMode.isActive = true;
      this.mappingMode.sourceField = subField.key;
      this.mappingMode.sourceFieldType = subField.type;
      this.mappingMode.isGroup = true;
      this.mappingMode.groupField = field.key;

      if (!this.selectedMappings[field.key]) {
        this.selectedMappings[field.key] = {
          targetField: "",
          isGroup: true,
          groupMappings: {},
          consistency: false,
        };
      }
      if (!this.selectedMappings[field.key].groupMappings[subField.key]) {
        this.selectedMappings[field.key].groupMappings[subField.key] = {
          targetField: "",
          consistency: false,
        };
      }
    },
    selectTargetField(field) {
      if (!this.mappingMode.isActive) return;

      if (this.mappingMode.isGroup) {
        const groupField = this.mappingMode.groupField;
        const sourceField = this.mappingMode.sourceField;
        this.selectedMappings[groupField].groupMappings[
          sourceField
        ].targetField = field.key;
      } else {
        const sourceField = this.mappingMode.sourceField;
        this.selectedMappings[sourceField].targetField = field.key;
      }

      this.mappingMode.isActive = false;
      this.mappingMode.sourceField = null;
      this.mappingMode.isGroup = false;
      this.mappingMode.groupField = null;
    },
    selectTargetSubField(field, subField) {
      if (!this.mappingMode.isActive) return;

      if (this.mappingMode.isGroup) {
        const groupField = this.mappingMode.groupField;
        const sourceField = this.mappingMode.sourceField;
        this.selectedMappings[groupField].groupMappings[
          sourceField
        ].targetField = subField.key;
      }

      this.mappingMode.isActive = false;
      this.mappingMode.sourceField = null;
      this.mappingMode.isGroup = false;
      this.mappingMode.groupField = null;
    },
    cancelMapping() {
      this.mappingMode.isActive = false;
      this.mappingMode.sourceField = null;
      this.mappingMode.isGroup = false;
      this.mappingMode.groupField = null;
    },
    isFieldMapped(fieldKey) {
      return Object.values(this.selectedMappings).some(
        (mapping) => mapping.targetField === fieldKey
      );
    },
    isSubFieldMapped(fieldKey, subFieldKey) {
      return Object.values(this.selectedMappings).some(
        (mapping) =>
          mapping.isGroup &&
          Object.values(mapping.groupMappings).some(
            (groupMapping) => groupMapping.targetField === subFieldKey
          )
      );
    },
    async saveMapping() {
      try {
        let mappingData;

        if (this.mappingDirection === "AtoB") {
          // Mapeo A → B (dirección original)
          mappingData = {
            sourceModel: this.selectedReference.sourceModel,
            sourceField: this.selectedReference.sourceField,
            targetModel: this.selectedReference.targetModel,
            mappings: Object.entries(this.selectedMappings).map(
              ([sourceField, mapping]) => ({
                sourceField,
                targetField: mapping.targetField,
                isGroup: mapping.isGroup,
                groupMappings: Object.entries(mapping.groupMappings || {}).map(
                  ([sourceField, groupMapping]) => ({
                    sourceField,
                    targetField: groupMapping.targetField,
                    consistency: groupMapping.consistency,
                  })
                ),
                consistency: mapping.consistency,
              })
            ),
          };
        } else if (this.mappingDirection === "BtoA") {
          // Mapeo B → A (dirección inversa)
          mappingData = {
            sourceModel: this.selectedReference.targetModel,
            sourceField: this.selectedReference.sourceField,
            targetModel: this.selectedReference.sourceModel,
            mappings: Object.entries(this.selectedMappings).map(
              ([sourceField, mapping]) => ({
                sourceField,
                targetField: mapping.targetField,
                isGroup: mapping.isGroup,
                groupMappings: Object.entries(mapping.groupMappings || {}).map(
                  ([sourceField, groupMapping]) => ({
                    sourceField,
                    targetField: groupMapping.targetField,
                    consistency: groupMapping.consistency,
                  })
                ),
                consistency: mapping.consistency,
              })
            ),
          };
        } else if (this.mappingDirection === "bidirectional") {
          // Mapeo bidireccional - guardar ambos mapeos
          const mappingAtoB = {
            sourceModel: this.selectedReference.sourceModel,
            sourceField: this.selectedReference.sourceField,
            targetModel: this.selectedReference.targetModel,
            mappings: Object.entries(this.selectedMappings).map(
              ([sourceField, mapping]) => ({
                sourceField,
                targetField: mapping.targetField,
                isGroup: mapping.isGroup,
                groupMappings: Object.entries(mapping.groupMappings || {}).map(
                  ([sourceField, groupMapping]) => ({
                    sourceField,
                    targetField: groupMapping.targetField,
                    consistency: groupMapping.consistency,
                  })
                ),
                consistency: mapping.consistency,
              })
            ),
          };

          const mappingBtoA = {
            sourceModel: this.selectedReference.targetModel,
            sourceField: this.selectedReference.sourceField,
            targetModel: this.selectedReference.sourceModel,
            mappings: Object.entries(this.selectedMappings).map(
              ([sourceField, mapping]) => ({
                sourceField,
                targetField: mapping.targetField,
                isGroup: mapping.isGroup,
                groupMappings: Object.entries(mapping.groupMappings || {}).map(
                  ([sourceField, groupMapping]) => ({
                    sourceField,
                    targetField: groupMapping.targetField,
                    consistency: groupMapping.consistency,
                  })
                ),
                consistency: mapping.consistency,
              })
            ),
          };

          // Log para verificar datos antes de enviar
          console.log("Enviando mapeo bidireccional AtoB:", mappingAtoB);
          console.log("Enviando mapeo bidireccional BtoA:", mappingBtoA);

          // Guardar ambos mapeos
          await wencesApi.post(
            "/api/gotrading/model-reference-mapping",
            mappingAtoB
          );
          await wencesApi.post(
            "/api/gotrading/model-reference-mapping",
            mappingBtoA
          );

          await Swal.fire({
            title: "¡Éxito!",
            text: "Mapeos bidireccionales guardados correctamente",
            icon: "success",
          });

          // Recargar los mapeos existentes después de guardar
          await this.loadExistingMappings();
          return;
        }

        // Log para verificar datos antes de enviar
        console.log("Enviando datos de mapeo:", mappingData);
        console.log("SelectedMappings actuales:", this.selectedMappings);

        if (this.currentMapping) {
          await wencesApi.put(
            `/api/gotrading/model-reference-mapping/${this.currentMapping._id}`,
            mappingData
          );
        } else {
          await wencesApi.post(
            "/api/gotrading/model-reference-mapping",
            mappingData
          );
        }

        await Swal.fire({
          title: "¡Éxito!",
          text: "Mapeo guardado correctamente",
          icon: "success",
        });

        // Recargar los mapeos existentes después de guardar
        await this.loadExistingMappings();
      } catch (error) {
        console.error("Error saving mapping:", error);
        await Swal.fire({
          title: "Error",
          text: "No se pudo guardar el mapeo",
          icon: "error",
        });
      }
    },
    async reloadFieldsForDirection() {
      if (!this.selectedReference) return;

      try {
        // Cargar campos del modelo origen según la nueva dirección
        const sourceModel =
          this.mappingDirection === "BtoA"
            ? this.selectedReference.targetModel
            : this.selectedReference.sourceModel;

        const sourceResponse = await wencesApi.get(
          `/api/gotrading/model-reference-mapping/model-fields/${sourceModel}`
        );
        if (sourceResponse.data && sourceResponse.data.success) {
          this.sourceFields = sourceResponse.data.data || [];
        }

        // Cargar campos del modelo destino según la nueva dirección
        const targetModel =
          this.mappingDirection === "BtoA"
            ? this.selectedReference.sourceModel
            : this.selectedReference.targetModel;

        const targetResponse = await wencesApi.get(
          `/api/gotrading/model-reference-mapping/model-fields/${targetModel}`
        );
        if (targetResponse.data && targetResponse.data.success) {
          this.targetFields = targetResponse.data.data || [];
        }

        // Cargar mapeos existentes para la nueva dirección
        await this.loadExistingMappingsForDirection();
      } catch (error) {
        console.error("Error reloading fields for direction:", error);
      }
    },
    async loadExistingMappingsForDirection() {
      try {
        // Determinar los modelos según la dirección actual
        const sourceModel =
          this.mappingDirection === "BtoA"
            ? this.selectedReference.targetModel
            : this.selectedReference.sourceModel;

        const targetModel =
          this.mappingDirection === "BtoA"
            ? this.selectedReference.sourceModel
            : this.selectedReference.targetModel;

        // Buscar mapeo existente para la nueva dirección
        const mappingsResponse = await wencesApi.get(
          "/api/gotrading/model-reference-mapping"
        );
        if (mappingsResponse.data && mappingsResponse.data.success) {
          const existingMapping = mappingsResponse.data.data.find(
            (m) =>
              m.sourceModel === sourceModel &&
              m.sourceField === this.selectedReference.sourceField &&
              m.targetModel === targetModel
          );

          if (existingMapping) {
            this.currentMapping = existingMapping;

            // Inicializar selectedMappings con los valores existentes
            const initialMappings = {};
            existingMapping.mappings.forEach((mapping) => {
              // Inicializar mapeos de grupo con consistencia
              const groupMappings = {};
              if (mapping.isGroup && mapping.groupMappings) {
                Object.entries(mapping.groupMappings).forEach(
                  ([subField, groupMapping]) => {
                    groupMappings[subField] = {
                      targetField: groupMapping.targetField,
                      consistency: false,
                    };
                  }
                );
              }

              initialMappings[mapping.sourceField] = {
                targetField: mapping.targetField,
                isGroup: mapping.isGroup,
                groupMappings: groupMappings,
                consistency: false,
              };
            });

            this.$set(this, "selectedMappings", initialMappings);
            console.log(
              "Mapeos cargados para dirección:",
              this.mappingDirection,
              initialMappings
            );
          } else {
            // No hay mapeo existente para esta dirección
            this.currentMapping = null;
            this.$set(this, "selectedMappings", {});
            console.log(
              "No hay mapeos existentes para dirección:",
              this.mappingDirection
            );
          }
        }
      } catch (error) {
        console.error("Error loading existing mappings for direction:", error);
      }
    },
    clearSelection() {
      this.$set(this, "selectedMappings", {});
    },
    getSourcePosition(fieldId) {
      const element = document.getElementById(`source-${fieldId}`);
      if (element) {
        const rect = element.getBoundingClientRect();
        const container = element.closest(".mapping-container");
        const containerRect = container.getBoundingClientRect();
        return {
          x: rect.right - containerRect.left,
          y: rect.top - containerRect.top + rect.height / 2,
        };
      }
      return { x: 0, y: 0 };
    },
    getTargetPosition(fieldId) {
      const element = document.getElementById(`target-${fieldId}`);
      if (element) {
        const rect = element.getBoundingClientRect();
        const container = element.closest(".mapping-container");
        const containerRect = container.getBoundingClientRect();
        return {
          x: rect.left - containerRect.left,
          y: rect.top - containerRect.top + rect.height / 2,
        };
      }
      return { x: 0, y: 0 };
    },
    updateMappingPositions() {
      this.$nextTick(() => {
        this.$forceUpdate();
      });
    },
    getFieldLabel(fieldKey, fields) {
      const field = fields.find((f) => f.key === fieldKey);
      return field ? field.label : fieldKey;
    },
    getSubFieldLabel(groupKey, subFieldKey, fields) {
      const group = fields.find((f) => f.key === groupKey);
      if (group && group.fields) {
        const subField = group.fields.find((f) => f.key === subFieldKey);
        return subField ? subField.label : subFieldKey;
      }
      return subFieldKey;
    },
    getMappedGroupFields(groupMappings) {
      return Object.entries(groupMappings)
        .filter(([_, mapping]) => mapping.targetField)
        .reduce((acc, [key, mapping]) => {
          acc[key] = mapping;
          return acc;
        }, {});
    },
    hasMappingsForReference(reference) {
      return this.existingMappings.some(
        (mapping) =>
          (mapping.sourceModel === reference.sourceModel &&
            mapping.sourceField === reference.sourceField &&
            mapping.targetModel === reference.targetModel) ||
          (mapping.sourceModel === reference.targetModel &&
            mapping.sourceField === reference.sourceField &&
            mapping.targetModel === reference.sourceModel)
      );
    },
    getMappingCount(reference) {
      // Buscar mapeo A → B
      const mappingAtoB = this.existingMappings.find(
        (m) =>
          m.sourceModel === reference.sourceModel &&
          m.sourceField === reference.sourceField &&
          m.targetModel === reference.targetModel
      );

      // Buscar mapeo B → A
      const mappingBtoA = this.existingMappings.find(
        (m) =>
          m.sourceModel === reference.targetModel &&
          m.sourceField === reference.sourceField &&
          m.targetModel === reference.sourceModel
      );

      let count = 0;

      // Contar mapeos de A → B
      if (mappingAtoB && mappingAtoB.mappings) {
        count += mappingAtoB.mappings.length;
        mappingAtoB.mappings.forEach((m) => {
          if (m.isGroup && m.groupMappings) {
            count += Object.keys(m.groupMappings).length;
          }
        });
      }

      // Contar mapeos de B → A
      if (mappingBtoA && mappingBtoA.mappings) {
        count += mappingBtoA.mappings.length;
        mappingBtoA.mappings.forEach((m) => {
          if (m.isGroup && m.groupMappings) {
            count += Object.keys(m.groupMappings).length;
          }
        });
      }

      return count;
    },
    getMappingDirectionText(reference) {
      // Buscar mapeo A → B
      const hasAtoB = this.existingMappings.some(
        (m) =>
          m.sourceModel === reference.sourceModel &&
          m.sourceField === reference.sourceField &&
          m.targetModel === reference.targetModel
      );

      // Buscar mapeo B → A
      const hasBtoA = this.existingMappings.some(
        (m) =>
          m.sourceModel === reference.targetModel &&
          m.sourceField === reference.sourceField &&
          m.targetModel === reference.sourceModel
      );

      if (hasAtoB && hasBtoA) {
        return "Bidireccional";
      } else if (hasAtoB) {
        return `${reference.sourceModel} → ${reference.targetModel}`;
      } else if (hasBtoA) {
        return `${reference.targetModel} → ${reference.sourceModel}`;
      }

      return "";
    },
    updateConsistency(sourceField, _isConsistent) {
      if (this.selectedMappings[sourceField]) {
        this.selectedMappings[sourceField].consistency = false;
      }
    },
    updateGroupConsistency(sourceField, subFieldKey, _isConsistent) {
      if (
        this.selectedMappings[sourceField] &&
        this.selectedMappings[sourceField].groupMappings
      ) {
        this.selectedMappings[sourceField].groupMappings[
          subFieldKey
        ].consistency = false;
      }
    },
    hasConsistencyMappings(reference) {
      // Buscar mapeo A → B
      const mappingAtoB = this.existingMappings.find(
        (m) =>
          m.sourceModel === reference.sourceModel &&
          m.sourceField === reference.sourceField &&
          m.targetModel === reference.targetModel
      );

      // Buscar mapeo B → A
      const mappingBtoA = this.existingMappings.find(
        (m) =>
          m.sourceModel === reference.targetModel &&
          m.sourceField === reference.sourceField &&
          m.targetModel === reference.sourceModel
      );

      if (mappingAtoB && mappingAtoB.mappings) {
        for (const mapping of mappingAtoB.mappings) {
          if (mapping.consistency) {
            return true;
          }
          if (mapping.isGroup && mapping.groupMappings) {
            for (const groupMapping of Object.values(mapping.groupMappings)) {
              if (groupMapping.consistency) {
                return true;
              }
            }
          }
        }
      }

      if (mappingBtoA && mappingBtoA.mappings) {
        for (const mapping of mappingBtoA.mappings) {
          if (mapping.consistency) {
            return true;
          }
          if (mapping.isGroup && mapping.groupMappings) {
            for (const groupMapping of Object.values(mapping.groupMappings)) {
              if (groupMapping.consistency) {
                return true;
              }
            }
          }
        }
      }

      return false;
    },
    getConsistencySummary() {
      let total = 0;
      let enabled = 0;

      Object.values(this.selectedMappings).forEach((mapping) => {
        if (mapping.isGroup) {
          Object.values(mapping.groupMappings).forEach((groupMapping) => {
            total++;
            if (groupMapping.consistency) {
              enabled++;
            }
          });
        } else {
          total++;
          if (mapping.consistency) {
            enabled++;
          }
        }
      });

      return { total, enabled };
    },
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.updateMappingPositions);
    window.removeEventListener("resize", this.updateMappingPositions);
  },
};
</script>

<style scoped>
.list-group-item {
  cursor: pointer;
}

.list-group-item:hover {
  background-color: #f8f9fa;
}

.list-group-item.active {
  background-color: #0d6efd;
  border-color: #0d6efd;
}

.field-mapping-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  margin-bottom: 8px;
  transition: all 0.3s ease;
}

.field-mapping-card:hover {
  background-color: #f8f9fa;
}

.field-mapping-card.is-mapped {
  border-color: #0d6efd;
  background-color: #e7f1ff;
}

.sub-field {
  background-color: #f8f9fa;
  border-left: 3px solid #6c757d;
}

.mapping-arrows {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.mapping-arrow {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #0d6efd;
}

.mapping-label {
  font-size: 0.8rem;
}

.field-info {
  flex: 1;
}

.mapping-controls {
  margin-left: 10px;
}

.btn-outline-primary.active {
  background-color: #0d6efd;
}

.field-mapping-card.is-selected {
  border-color: #198754;
  background-color: #d1e7dd;
}

.btn-primary {
  background-color: #198754;
  border-color: #198754;
}

.btn-primary:hover {
  background-color: #157347;
  border-color: #146c43;
}

.mapping-container {
  position: relative;
}

.mapping-connections {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.mapping-svg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: visible;
}

.field-mapping-card.is-mapped::after {
  content: "";
  position: absolute;
  top: 50%;
  right: -10px;
  width: 10px;
  height: 2px;
  background-color: #0d6efd;
}

.sub-field.is-mapped::after {
  right: -13px;
}

.mapping-panel {
  width: 100%;
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 4px;
  max-height: 600px;
  overflow-y: auto;
}

.mapping-item {
  margin-bottom: 15px;
}

.mapping-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  background-color: white;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.sub-mapping {
  margin-left: 20px;
  background-color: #f8f9fa;
}

.mapping-field {
  flex: 1;
  padding: 4px 8px;
  font-size: 0.9rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mapping-field.source {
  color: #0d6efd;
  font-weight: 500;
}

.mapping-field.target {
  color: #198754;
  font-weight: 500;
}

.mapping-arrow {
  padding: 0 4px;
  min-width: 20px;
  text-align: center;
}

.mapping-arrow i {
  font-size: 0.8rem;
}

.mapping-consistency {
  margin-left: 8px;
  min-width: 80px;
}

.mapping-consistency .form-check {
  margin: 0;
  display: flex;
  align-items: center;
}

.mapping-consistency .form-check-input {
  margin: 0;
  margin-right: 4px;
}

.mapping-consistency .form-check-label {
  margin: 0;
  font-size: 0.75rem;
  color: #6c757d;
}

.mapping-consistency .form-check-label:hover {
  color: #495057;
}

.mapping-consistency .form-check-input:checked {
  background-color: #198754;
  border-color: #198754;
}

.mapping-consistency .form-check-input:checked:focus {
  box-shadow: 0 0 0 0.25rem rgba(25, 135, 84, 0.25);
}

.mapping-row.consistency-enabled {
  border-left: 3px solid #198754;
  background-color: #f8fff9;
}

.mapping-row.consistency-enabled .mapping-consistency .form-check-label {
  color: #198754;
  font-weight: 500;
}

.list-group-item.has-mappings {
  border-left: 3px solid #198754;
  background-color: #f8f9fa;
}

.list-group-item.has-mappings:hover {
  background-color: #e9ecef;
}

.mapping-badge {
  font-size: 0.75rem;
  color: #198754;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.mapping-badge i {
  font-size: 0.7rem;
}

.mapping-badge .text-success {
  color: #198754 !important;
}

.list-group-item.active .mapping-badge {
}
</style>
